
import { use, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';
import Header from '../../components/feature/Header';
import type { User } from '../../models/user';

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);

  // Mock user data


  const planData = {
    currentPlan: 'Pro',
    billingCycle: 'Monthly',
    nextBilling: 'February 15, 2024',
    price: '$29.99',
    features: [
      'Unlimited AI interviews',
      'Advanced analytics',
      'Video recording',
      'Custom feedback reports',
      'Priority support'
    ],
    usage: {
      interviews: { used: 12, limit: 'Unlimited' },
      storage: { used: 2.4, limit: 10, unit: 'GB' },
      reports: { used: 8, limit: 'Unlimited' }
    }
  };

  const notificationSettings = {
    emailNotifications: true,
    interviewReminders: true,
    weeklyReports: false,
    marketingEmails: false,
    browserNotifications: true
  };

  const privacySettings = {
    profileVisibility: 'private',
    dataSharing: false,
    analyticsTracking: true,
    cookiePreferences: 'essential'
  };

  const ProgressBar = ({ used, limit, unit = '' }: { used: number; limit: number | string; unit?: string }) => {
    if (limit === 'Unlimited') {
      return (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">{used} used</span>
          <span className="text-sm font-medium text-green-600">Unlimited</span>
        </div>
      );
    }

    const percentage = (used / (limit as number)) * 100;
    const isNearLimit = percentage > 80;

    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">{used}{unit} used</span>
          <span className="text-sm text-gray-600">{limit}{unit} total</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${isNearLimit ? 'bg-red-500' : 'bg-blue-500'}`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          ></div>
        </div>
      </div>
    );
  };


  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      setUser(JSON.parse(stored));
    }


  }, []);
  return (
    <div className="min-h-screen bg-gray-50">
      <Header showAuth={false} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
            <p className="text-lg text-gray-600">Manage your account and preferences</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Dashboard
          </Button>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card padding="sm">
              <nav className="space-y-1">
                {[
                  { id: 'profile', label: 'Profile', icon: 'ri-user-line' },
                  // { id: 'plan', label: 'Plan & Billing', icon: 'ri-vip-crown-line' },
                  // { id: 'notifications', label: 'Notifications', icon: 'ri-notification-line' },
                  // { id: 'privacy', label: 'Privacy', icon: 'ri-shield-line' },
                  // { id: 'preferences', label: 'Preferences', icon: 'ri-settings-line' },
                  { id: 'support', label: 'Support', icon: 'ri-customer-service-line' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                  >
                    <i className={`${tab.icon} mr-3`}></i>
                    {tab.label}
                  </button>
                ))}
              </nav>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <Card padding="lg">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Profile Information</h2>

                  <div className="flex items-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-6">
                      <span className="text-2xl font-bold text-white">
                        {user?.name
                          ?.split(' ')
                          .filter(Boolean)
                          .slice(0, 2)
                          .map(word => word.charAt(0).toUpperCase())
                          .join('')}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{user?.name}</h3>
                      {/* <p className="text-gray-600">Member since {user?.created_at}</p> */}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{user?.name}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{user?.email}</div>
                    </div>
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{user?.phone}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                      <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{user?.location}</div>
                    </div> */}
                  </div>
                </Card>

                {/* <Card padding="lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Statistics</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-2">{userData.totalInterviews}</div>
                      <div className="text-sm text-gray-600">Total Interviews</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600 mb-2">{userData.averageScore}</div>
                      <div className="text-sm text-gray-600">Average Score</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600 mb-2">7</div>
                      <div className="text-sm text-gray-600">Days Streak</div>
                    </div>
                  </div>
                </Card> */}
              </div>
            )}

            {activeTab === 'plan' && (
              <div className="space-y-6">
                <Card padding="lg">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">Current Plan</h2>
                    <span className="px-3 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full text-sm font-medium">
                      {planData.currentPlan}
                    </span>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <div className="mb-6">
                        <div className="text-3xl font-bold text-gray-900 mb-2">{planData.price}</div>
                        <div className="text-gray-600">{planData.billingCycle} billing</div>
                        <div className="text-sm text-gray-500">Next billing: {planData.nextBilling}</div>
                      </div>

                      <div className="space-y-3">
                        <h4 className="font-semibold text-gray-900">Plan Features:</h4>
                        {planData.features.map((feature, index) => (
                          <div key={index} className="flex items-center">
                            <i className="ri-check-line text-green-500 mr-2"></i>
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-4">Usage This Month</h4>
                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Interviews</span>
                          </div>
                          <ProgressBar used={planData.usage.interviews.used} limit={planData.usage.interviews.limit} />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Storage</span>
                          </div>
                          <ProgressBar used={planData.usage.storage.used} limit={planData.usage.storage.limit} unit=" GB" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Reports Generated</span>
                          </div>
                          <ProgressBar used={planData.usage.reports.used} limit={planData.usage.reports.limit} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900">Want more features?</h4>
                        <p className="text-gray-600 text-sm">Upgrade to Enterprise for advanced analytics and team features</p>
                      </div>
                      <Button>
                        <i className="ri-vip-crown-line mr-2"></i>
                        Upgrade Plan
                      </Button>
                    </div>
                  </div>
                </Card>

                <Card padding="lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing History</h3>
                  <div className="space-y-3">
                    {[
                      { date: 'Jan 15, 2024', amount: '$29.99', status: 'Paid' },
                      { date: 'Dec 15, 2023', amount: '$29.99', status: 'Paid' },
                      { date: 'Nov 15, 2023', amount: '$29.99', status: 'Paid' }
                    ].map((bill, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">{bill.date}</div>
                          <div className="text-sm text-gray-600">Pro Plan - Monthly</div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-gray-900">{bill.amount}</div>
                          <div className="text-sm text-green-600">{bill.status}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {activeTab === 'notifications' && (
              <Card padding="lg">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Notification Preferences</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h3>
                    <div className="space-y-4">
                      {[
                        { key: 'emailNotifications', label: 'General email notifications', description: 'Receive important updates about your account' },
                        { key: 'interviewReminders', label: 'Interview reminders', description: 'Get reminded about scheduled practice sessions' },
                        { key: 'weeklyReports', label: 'Weekly progress reports', description: 'Summary of your interview performance and improvements' },
                        { key: 'marketingEmails', label: 'Marketing emails', description: 'Tips, features, and promotional content' }
                      ].map((setting) => (
                        <div key={setting.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{setting.label}</div>
                            <div className="text-sm text-gray-600">{setting.description}</div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              defaultChecked={notificationSettings[setting.key as keyof typeof notificationSettings]}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Browser Notifications</h3>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">Push notifications</div>
                        <div className="text-sm text-gray-600">Receive notifications in your browser</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          defaultChecked={notificationSettings.browserNotifications}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'privacy' && (
              <Card padding="lg">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Privacy & Security</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Data & Privacy</h3>
                    <div className="space-y-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">Profile Visibility</span>
                          <select className="border border-gray-300 rounded-lg px-3 py-1 text-sm">
                            <option value="private">Private</option>
                            <option value="public">Public</option>
                          </select>
                        </div>
                        <p className="text-sm text-gray-600">Control who can see your profile information</p>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">Data sharing with partners</div>
                          <div className="text-sm text-gray-600">Allow sharing anonymized data for research</div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            defaultChecked={privacySettings.dataSharing}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">Analytics tracking</div>
                          <div className="text-sm text-gray-600">Help us improve by tracking usage patterns</div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            defaultChecked={privacySettings.analyticsTracking}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Security</h3>
                    <div className="space-y-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">Change Password</div>
                            <div className="text-sm text-gray-600">Last changed 3 months ago</div>
                          </div>
                          <Button variant="outline" size="sm">
                            Update
                          </Button>
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">Two-Factor Authentication</div>
                            <div className="text-sm text-gray-600">Add an extra layer of security</div>
                          </div>
                          <Button variant="outline" size="sm">
                            Enable
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'preferences' && (
              <Card padding="lg">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Interview Preferences</h2>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Default Settings</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Interview Type</label>
                        <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                          <option value="ai">AI Interview</option>
                          <option value="video">Video Interview</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Default Duration</label>
                        <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                          <option value="15">15 minutes</option>
                          <option value="30">30 minutes</option>
                          <option value="45">45 minutes</option>
                          <option value="60">60 minutes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty Level</label>
                        <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                          <option value="beginner">Beginner</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="advanced">Advanced</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                        <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                          <option value="en">English</option>
                          <option value="es">Spanish</option>
                          <option value="fr">French</option>
                          <option value="de">German</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Feedback Settings</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">Detailed feedback</div>
                          <div className="text-sm text-gray-600">Get comprehensive analysis of your responses</div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" defaultChecked />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">Real-time suggestions</div>
                          <div className="text-sm text-gray-600">Get hints during the interview</div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'support' && (
              <div className="space-y-6">
                <Card padding="lg">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Help & Support</h2>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center">
                          <i className="ri-question-line text-blue-600 text-xl mr-3"></i>
                          <div>
                            <div className="font-medium text-gray-900">FAQ</div>
                            <div className="text-sm text-gray-600">Find answers to common questions</div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center">
                          <i className="ri-book-line text-green-600 text-xl mr-3"></i>
                          <div>
                            <div className="font-medium text-gray-900">User Guide</div>
                            <div className="text-sm text-gray-600">Learn how to use all features</div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <div className="flex items-center">
                          <i className="ri-mail-line text-purple-600 text-xl mr-3"></i>
                          <div>
                            <div className="font-medium text-gray-900">Contact Support</div>
                            <div className="text-sm text-gray-600">Get help from our team</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Contact</h3>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <i className="ri-mail-line text-gray-400 mr-3"></i>
                          <span className="text-gray-700">support@pitchme.ai</span>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-time-line text-gray-400 mr-3"></i>
                          <span className="text-gray-700">Response time: 24 hours</span>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-chat-3-line text-gray-400 mr-3"></i>
                          <span className="text-gray-700">Live chat available</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card padding="lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">App Version</div>
                      <div className="font-medium text-gray-900">2.1.4</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Last Updated</div>
                      <div className="font-medium text-gray-900">January 15, 2024</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Browser</div>
                      <div className="font-medium text-gray-900">Chrome 120.0</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Platform</div>
                      <div className="font-medium text-gray-900">Web</div>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
